/**
 * Brazilian phone numbers, as a person reads them.
 *
 * The database stores digits — `5511900181018` — because that is what WhatsApp addresses with, and
 * Contatos printed exactly that. `src/server/services/phone.ts` already had a display helper, and
 * `Conversas.tsx` had grown a second one locally with a comment explaining why: the server module
 * is not importable from the browser. That is what `src/shared` is for, so the normalising half
 * moves here and both sides import it.
 *
 * Two formats, deliberately:
 *
 *   * `displayPhone` writes **(84) 99999-9999**, the way a number is written in Brazil, for every
 *     screen in the app. The reader is a nutritionist in Natal looking at patients in Natal; the
 *     country code is noise she has to read past on every row.
 *   * `formatBR` (still in `services/phone.ts`) writes **+55 84 99999-9999** and stays where it is,
 *     for the three server-side strings that end up in WhatsApp or in a note. Switching those is a
 *     separate change in a file another slice is holding; `docs/15-backlog.md` carries it.
 *
 * Pure, so it lives here with `time.ts`, `colour.ts` and `sparkline.ts`.
 */

const digits = (raw: string) => raw.replace(/\D/g, "");

/**
 * Normalises a typed number to E.164-ish digits with the Brazilian country code.
 *
 * The trap: a naive `startsWith("55")` reads **DDD 55 (Santa Maria, RS)** as the country code, so
 * `55 9xxxx-xxxx` becomes a mangled foreign number. Length is what disambiguates — a Brazilian
 * number with country code is 12 or 13 digits, without it 10 or 11.
 *
 * Moved here from `services/phone.ts`, which re-exports it so the WhatsApp call sites are
 * unchanged. One definition, two consumers.
 */
export function normalizeBR(raw: string): string {
  const d = digits(raw);
  if (!d) return "";
  // Already carries the country code: 55 + 2-digit DDD + 8 or 9 subscriber digits.
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) return d;
  // Bare national number: 2-digit DDD + 8 or 9 digits. This is where DDD 55 lives.
  if (d.length === 10 || d.length === 11) return `55${d}`;
  // Anything else is foreign or already international; leave it alone.
  return d;
}

/**
 * Display form: **(84) 99999-9999** for a Brazilian number, mobile or landline.
 *
 * Anything that is not Brazilian-shaped keeps a leading `+` and its digits rather than being
 * forced into a mask it does not fit — a 15-digit `@lid` identity, a foreign number, a partial one
 * somebody typed. Showing it unmangled is honest; `(15) 1234-56789` is not.
 *
 * Empty input gives an empty string, so a caller can write `displayPhone(p) || "—"` without
 * first checking.
 */
export function displayPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  /*
   * A leading `+` is information, and normalising would throw it away.
   *
   * `normalizeBR` reads any 10- or 11-digit number as Brazilian without a country code, which is
   * the right call when the job is to *address* someone on WhatsApp in a Brazil-only product. For
   * display it mangles the one case where the writer said otherwise: `+1 415 555 0132` is eleven
   * digits, so it would come back as "(14) 15555-0132" — a Bauru number that does not exist.
   * A `+` with anything other than a Brazilian-length 55 number is shown as it was given.
   */
  const typed = digits(raw);
  const brWithCode = (typed.length === 12 || typed.length === 13) && typed.startsWith("55");
  if (raw.trimStart().startsWith("+") && !brWithCode) return `+${typed}`;

  const d = normalizeBR(raw);
  if (!d) return "";
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) {
    const ddd = d.slice(2, 4);
    const rest = d.slice(4);
    const split = rest.length === 9
      ? `${rest.slice(0, 5)}-${rest.slice(5)}`
      : `${rest.slice(0, 4)}-${rest.slice(4)}`;
    return `(${ddd}) ${split}`;
  }
  return `+${d}`;
}

/**
 * Whether this "name" is really a phone number wearing one.
 *
 * Contacts created from an inbound message fall back to the number as their name, so `name` is
 * rarely null — and the initials of "+55 84 9636-6662" come out as "+8". A name has letters in it;
 * anything else is a number. Lived in `Conversas.tsx`; Contatos and Pacientes need the same test.
 */
export const hasRealName = (name: string | null | undefined) =>
  Boolean(name && /\p{L}/u.test(name));

/**
 * The mask while typing (consistency sweep, 22/10): "(84) 99999-9999" for a mobile, "(84) 3333-4444"
 * for a landline, the same on every phone field. A number pasted with +55 loses the country code
 * here, because the person typing it never thinks in it; the server adds it back (`normalizeBR`).
 */
export function maskPhone(raw: string): string {
  let d = digits(raw);
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  const ddd = d.slice(0, 2);
  const rest = d.slice(2);
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  const head = rest.length > 8 ? rest.slice(0, 5) : rest.slice(0, 4);
  return `(${ddd}) ${head}-${rest.slice(head.length)}`;
}

